'use client';

import { useEffect, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

type Counter = { id: number; name: string; value: number };

const STORE = 'cleanor-tally-v1';

// A focused button fires its click on Space keyup. The page already handles Space itself, so
// stop the button from also acting (it could be "Reset all").
function swallowButtonSpace(e: KeyboardEvent) {
  if (e.code === 'Space' && (e.target as HTMLElement).closest('button')) e.preventDefault();
}

function load(): Counter[] {
  try {
    const raw = window.localStorage.getItem(STORE);
    const parsed = raw ? (JSON.parse(raw) as Counter[]) : null;
    if (Array.isArray(parsed) && parsed.length) return parsed;
  } catch {
    // Storage blocked or corrupt: start fresh.
  }
  return [{ id: 1, name: 'Count', value: 0 }];
}

function Tally({ toolSlug }: { toolSlug: string }) {
  const [counters, setCounters] = useState<Counter[]>([{ id: 1, name: 'Count', value: 0 }]);
  const [active, setActive] = useState(1);
  const [step, setStep] = useState('1');
  const [ready, setReady] = useState(false);
  const reported = useRef(false);

  useEffect(() => {
    const c = load();
    setCounters(c);
    setActive(c[0].id);
    setReady(true);
  }, []);
  useEffect(() => {
    if (!ready) return;
    try {
      window.localStorage.setItem(STORE, JSON.stringify(counters));
    } catch {
      // Private mode: counting still works, it just is not remembered.
    }
  }, [counters, ready]);

  const inc = (id: number, by: number) => {
    if (!reported.current) {
      reported.current = true;
      void consumeToolUsage(toolSlug, '').catch(() => undefined);
    }
    setCounters((list) => list.map((c) => (c.id === id ? { ...c, value: c.value + by } : c)));
    if (by > 0 && navigator.vibrate) navigator.vibrate(8);
  };
  const s = Number(step) || 1;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest('input, textarea, select')) return;
      if (
        e.code === 'Space' ||
        e.code === 'ArrowUp' ||
        e.code === 'Enter' ||
        e.key === '+' ||
        e.code === 'NumpadAdd'
      ) {
        e.preventDefault();
        inc(active, s);
      } else if (
        e.code === 'ArrowDown' ||
        e.key === '-' ||
        e.code === 'NumpadSubtract' ||
        e.code === 'Backspace'
      ) {
        e.preventDefault();
        inc(active, -s);
      }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', swallowButtonSpace);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', swallowButtonSpace);
    };
  });

  const total = counters.reduce((a, c) => a + c.value, 0);

  return (
    <div className="tool-live-grid">
      <section className="tool-live-uploader">
        {counters.map((c) => (
          <div
            key={c.id}
            onClick={() => setActive(c.id)}
            style={{
              border: `2px solid ${c.id === active ? 'var(--accent, #2563eb)' : 'rgba(127,127,127,0.35)'}`,
              borderRadius: 12,
              padding: '0.75rem',
              display: 'grid',
              gap: '0.5rem',
            }}
          >
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                aria-label="Counter name"
                onChange={(e) =>
                  setCounters((l) =>
                    l.map((x) => (x.id === c.id ? { ...x, name: e.target.value } : x)),
                  )
                }
                style={{ flex: 1, minWidth: 0 }}
                type="text"
                value={c.name}
              />
              {counters.length > 1 ? (
                <button
                  aria-label={`Remove ${c.name}`}
                  className="button button-secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCounters((l) => l.filter((x) => x.id !== c.id));
                    if (active === c.id) setActive(counters.find((x) => x.id !== c.id)?.id ?? 1);
                  }}
                  type="button"
                >
                  ✕
                </button>
              ) : null}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button
                aria-label={`Decrease ${c.name}`}
                className="button button-secondary"
                onClick={() => inc(c.id, -s)}
                style={{ fontSize: '1.6rem', minWidth: '3.5rem' }}
                type="button"
              >
                −
              </button>
              <button
                aria-label={`Increase ${c.name}`}
                className="button"
                onClick={() => inc(c.id, s)}
                style={{
                  flex: 1,
                  fontSize: 'clamp(2.4rem, 9vw, 3.6rem)',
                  fontVariantNumeric: 'tabular-nums',
                  padding: '0.6rem',
                  touchAction: 'manipulation',
                }}
                type="button"
              >
                {c.value.toLocaleString('en-US')}
              </button>
            </div>
          </div>
        ))}
        <div className="tool-live-actions">
          <button
            className="button button-secondary"
            onClick={() => {
              const id = Math.max(...counters.map((c) => c.id)) + 1;
              setCounters((l) => [...l, { id, name: `Count ${l.length + 1}`, value: 0 }]);
              setActive(id);
            }}
            type="button"
          >
            Add counter
          </button>
          <button
            className="button button-secondary"
            onClick={() =>
              setCounters((l) => l.map((c) => (c.id === active ? { ...c, value: 0 } : c)))
            }
            type="button"
          >
            Reset selected
          </button>
          <button
            className="button button-secondary"
            onClick={() => setCounters((l) => l.map((c) => ({ ...c, value: 0 })))}
            type="button"
          >
            Reset all
          </button>
        </div>
      </section>
      <section className="tool-live-preview">
        <div className="tool-html-meta-grid">
          <label className="tool-live-field">
            <span>Step per tap</span>
            <input
              inputMode="numeric"
              onChange={(e) => setStep(e.target.value)}
              type="text"
              value={step}
            />
          </label>
          {counters.length > 1 ? (
            <div className="tool-live-field">
              <span>Total of all counters</span>
              <input readOnly type="text" value={total.toLocaleString('en-US')} />
            </div>
          ) : null}
        </div>
        <p className="tool-live-panel-note">
          Tap the number to count. Keyboard: Space, Enter or ↑ adds, ↓ or Backspace subtracts, on
          the selected counter. Counts are saved in this browser, so a reload does not lose them.
        </p>
      </section>
    </div>
  );
}

const DURATIONS = [5, 10, 30, 60];

function Spacebar({ toolSlug }: { toolSlug: string }) {
  const [duration, setDuration] = useState(10);
  const [count, setCount] = useState(0);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [now, setNow] = useState(0);
  const [result, setResult] = useState<{ count: number; secs: number } | null>(null);
  const [best, setBest] = useState<Record<number, number>>({});
  const reported = useRef(false);
  const countRef = useRef(0);

  useEffect(() => {
    try {
      setBest(JSON.parse(window.localStorage.getItem('cleanor-spacebar-best') ?? '{}'));
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (startedAt === null) return;
    const id = window.setInterval(() => {
      const t = performance.now();
      setNow(t);
      if (t - startedAt >= duration * 1000) {
        window.clearInterval(id);
        const final = countRef.current;
        setResult({ count: final, secs: duration });
        setStartedAt(null);
        setBest((b) => {
          const next = { ...b, [duration]: Math.max(b[duration] ?? 0, final) };
          try {
            window.localStorage.setItem('cleanor-spacebar-best', JSON.stringify(next));
          } catch {
            // ignore
          }
          return next;
        });
      }
    }, 50);
    return () => window.clearInterval(id);
  }, [startedAt, duration]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || e.repeat) return;
      const t = e.target as HTMLElement;
      if (t.closest('input, textarea, select')) return;
      e.preventDefault();
      if (result) return;
      if (startedAt === null) {
        if (!reported.current) {
          reported.current = true;
          void consumeToolUsage(toolSlug, '').catch(() => undefined);
        }
        countRef.current = 1;
        setCount(1);
        const t0 = performance.now();
        setStartedAt(t0);
        setNow(t0);
        return;
      }
      countRef.current += 1;
      setCount(countRef.current);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', swallowButtonSpace);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', swallowButtonSpace);
    };
  }, [startedAt, result, toolSlug]);

  const left = startedAt === null ? duration : Math.max(0, duration - (now - startedAt) / 1000);

  return (
    <div className="tool-live-grid">
      <section className="tool-live-uploader">
        <div className="tool-live-actions">
          {DURATIONS.map((d) => (
            <button
              className={d === duration ? 'button' : 'button button-secondary'}
              disabled={startedAt !== null}
              key={d}
              onClick={() => {
                setDuration(d);
                setResult(null);
                setCount(0);
              }}
              type="button"
            >
              {d} seconds
            </button>
          ))}
        </div>
        <div
          style={{
            border: '2px solid currentColor',
            borderRadius: 12,
            padding: '1.5rem 1rem',
            textAlign: 'center',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          <div style={{ fontSize: 'clamp(3rem, 12vw, 5rem)', fontWeight: 800, lineHeight: 1 }}>
            {count}
          </div>
          <div>
            {startedAt !== null
              ? `${left.toFixed(1)} s left`
              : result
                ? 'Time!'
                : 'Press Space to start'}
          </div>
        </div>
        <button
          className="button"
          onClick={() => {
            setResult(null);
            setCount(0);
            countRef.current = 0;
          }}
          type="button"
        >
          {result ? 'Try again' : 'Reset'}
        </button>
      </section>
      <section className="tool-live-preview">
        <div className="tool-html-meta-grid">
          {[
            ['Presses', result ? String(result.count) : String(count)],
            [
              'Presses per second',
              result
                ? (result.count / result.secs).toFixed(2)
                : // A rate over the first fraction of a second is noise (1 press in 0.02 s
                  // reads as 50/s), so show it only after a full second.
                  startedAt !== null && duration - left >= 1
                  ? (count / (duration - left)).toFixed(2)
                  : '—',
            ],
            [`Your best in ${duration} s`, best[duration] ? String(best[duration]) : '—'],
          ].map(([k, v]) => (
            <div className="tool-live-field" key={k}>
              <span>{k}</span>
              <input readOnly type="text" value={v} />
            </div>
          ))}
        </div>
        <p className="tool-live-panel-note">
          The timer starts with your first press. Holding the key down does not count; only separate
          presses do. Most people manage 6-8 presses a second.
        </p>
      </section>
    </div>
  );
}

export default function BrowserTallyCounter({
  config,
}: {
  config?: { toolSlug?: string; variant?: string; heading?: string };
}) {
  const spacebar = config?.variant === 'spacebar';
  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Counter</span>
        <h2 className="tool-live-title">{config?.heading ?? 'Tally counter'}</h2>
        <p className="tool-live-subtitle">
          {spacebar
            ? 'Press the spacebar as fast as you can before time runs out.'
            : 'Tap, click or press Space to count. Works offline.'}
        </p>
      </div>
      {spacebar ? (
        <Spacebar toolSlug={config?.toolSlug ?? 'spacebar-counter'} />
      ) : (
        <Tally toolSlug={config?.toolSlug ?? 'tally-counter'} />
      )}
    </div>
  );
}
